import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FiSunrise, FiSunset, FiAlertTriangle, FiCalendar, FiMoon, FiClock } from "react-icons/fi";
import { GiSparkles, GiYinYang, GiPrayerBeads } from "react-icons/gi";
import api from "../utils/api";
import Loader from "./Loader";
import OmSymbol from "./OmSymbol";

/**
 * Today's Panchang — rendered from the self-hosted /api/panchang route
 * (computed locally via the panchang-ts library for Thane, Maharashtra).
 *
 * Falls back to a calm, on-brand error card if the API is unreachable so
 * the homepage never breaks.
 */
export default function Panchang() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    api
      .get("/panchang")
      .then((res) => {
        if (active) setData(res.data);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  if (loading) return <Loader label="Loading today's Panchang..." />;

  if (error || !data) {
    return (
      <div className="max-w-2xl mx-auto rounded-2xl border border-gold-200 bg-white p-8 text-center shadow-premium">
        <div className="text-3xl mb-2 flex justify-center text-saffron-500"><OmSymbol size={32} /></div>
        <p className="font-heading text-maroon-800 mb-1">Panchang is temporarily unavailable</p>
        <p className="text-sm text-gray-500">Please check back shortly for today's auspicious timings.</p>
      </div>
    );
  }

  // Primary attributes — the five core elements of the Panchang
  const primary = [
    { label: "Vaar (Day)", value: data.vara, sub: data.varaHindi, icon: <FiCalendar className="inline" /> },
    { label: "Tithi", value: data.tithi, sub: data.tithiPaksha, icon: <FiMoon className="inline" /> },
    { label: "Nakshatra", value: data.nakshatra, sub: data.nakshatraPada, icon: <GiSparkles className="inline" /> },
    { label: "Yoga", value: data.yoga, sub: "", icon: <GiYinYang className="inline" /> },
    { label: "Karana", value: data.karana, sub: "", icon: <FiClock className="inline" /> },
    { label: "Maasa", value: data.masa, sub: "", icon: <GiPrayerBeads className="inline" /> },
  ];

  // Auspicious / inauspicious timings
  const timings = [
    { label: "Sunrise", value: data.sunrise, icon: <FiSunrise className="text-gold-500" />, tone: "good" },
    { label: "Sunset", value: data.sunset, icon: <FiSunset className="text-maroon-500" />, tone: "good" },
    { label: "Abhijit Muhurta", value: data.abhijitMuhurta, icon: <GiSparkles className="text-gold-500" />, tone: "good" },
    { label: "Rahu Kalam", value: data.rahuKalam, icon: <FiAlertTriangle className="text-red-500" />, tone: "warn" },
    { label: "Yamaganda", value: data.yamaganda, icon: <FiAlertTriangle className="text-orange-500" />, tone: "warn" },
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Date header */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="rounded-2xl bg-hero-gradient bg-mandala text-white p-6 mb-5 shadow-premium-lg text-center relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gold-radial pointer-events-none" />
        <p className="relative text-gold-300 text-xs uppercase tracking-[0.2em] mb-1">Today</p>
        <p className="relative font-heading text-xl sm:text-2xl text-gold-100">{data.formattedDate}</p>
        <p className="relative text-gold-100/70 text-xs mt-1">Computed for Thane, Maharashtra</p>
      </motion.div>

      {/* Primary panchang elements */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        {primary.map((item, i) => (
          <motion.div
            key={item.label}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.05 }}
            className="bg-white rounded-2xl border border-gold-100 shadow-premium p-4 text-center hover-lift"
          >
            <div className="text-2xl mb-1">{item.icon}</div>
            <p className="text-[11px] uppercase tracking-wider text-saffron-600 font-semibold mb-1">{item.label}</p>
            <p className="font-heading text-maroon-800 text-base leading-tight">{item.value}</p>
            {item.sub && <p className="text-xs text-gray-500 mt-0.5">{item.sub}</p>}
          </motion.div>
        ))}
      </div>

      {/* Timings */}
      <div className="bg-white rounded-2xl border border-gold-100 shadow-premium overflow-hidden">
        <div className="bg-saffron-50 px-5 py-3 border-b border-gold-100">
          <h4 className="font-heading text-maroon-800 flex items-center gap-2 text-sm sm:text-base">
            <FiClock className="text-saffron-500" /> Auspicious &amp; Inauspicious Timings
          </h4>
        </div>
        <div className="divide-y divide-gold-50">
          {timings.map((t) => (
            <div key={t.label} className="flex items-center justify-between px-5 py-3">
              <span className="flex items-center gap-2.5 text-sm text-gray-700">
                <span className="text-lg w-5 flex justify-center">{t.icon}</span>
                <span className={t.tone === "warn" ? "font-medium" : ""}>{t.label}</span>
              </span>
              <span
                className={`text-sm font-semibold ${
                  t.tone === "warn" ? "text-red-600" : "text-maroon-700"
                }`}
              >
                {t.value}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Festival banner (only when present) */}
      {data.festivals && data.festivals.length > 0 && (
        <div className="mt-4 flex items-center gap-3 bg-gradient-to-r from-saffron-50 to-gold-50 border border-gold-200 rounded-2xl px-5 py-3">
          <span className="text-2xl text-saffron-500"><GiSparkles size={24} /></span>
          <p className="text-sm text-maroon-800">
            <span className="font-semibold">Today's Festival: </span>
            {data.festivals.join(", ")}
          </p>
        </div>
      )}
    </div>
  );
}
